import React, { Suspense, lazy, useEffect, useRef, useState } from "react";

const QrCode = lazy(() => import("react-qr-code"));

const scheduleIdleLoad = (callback, fallbackDelay = 1400) => {
  if (typeof window === "undefined") {
    callback();
    return () => undefined;
  }

  if (typeof window.requestIdleCallback === "function") {
    const idleId = window.requestIdleCallback(callback, {
      timeout: fallbackDelay * 2,
    });

    return () => window.cancelIdleCallback(idleId);
  }

  const timeoutId = window.setTimeout(callback, fallbackDelay);
  return () => window.clearTimeout(timeoutId);
};

const QrPlaceholder = () => (
  <div className="contact-qr-placeholder" aria-hidden="true">
    <span className="contact-qr-placeholder-grid"></span>
  </div>
);

const LazyQrCode = ({ value }) => {
  const shellRef = useRef(null);
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") {
      setShouldRender(true);
      return undefined;
    }

    if (shouldRender) {
      return undefined;
    }

    let cleanupIdleTask = scheduleIdleLoad(() => {
      setShouldRender(true);
    }, 1800);
    let observer;

    if (typeof window.IntersectionObserver === "function" && shellRef.current) {
      observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting) {
            return;
          }

          cleanupIdleTask();
          setShouldRender(true);
          observer.disconnect();
        },
        {
          rootMargin: "240px 0px",
          threshold: 0,
        }
      );

      observer.observe(shellRef.current);
    }

    return () => {
      cleanupIdleTask();

      if (observer) {
        observer.disconnect();
      }
    };
  }, [shouldRender]);

  return (
    <div ref={shellRef} className="contact-qr-render">
      {shouldRender ? (
        <Suspense fallback={<QrPlaceholder />}>
          <QrCode
            value={value}
            size={232}
            level="H"
            bgColor="#ffffff"
            fgColor="#111111"
            style={{ width: "100%", height: "100%" }}
          />
        </Suspense>
      ) : (
        <QrPlaceholder />
      )}
    </div>
  );
};

export default LazyQrCode;
